import React, {FC, PropsWithChildren} from 'react';
import {Link} from 'react-router-dom';

import {EventStatus, GenericProps, MatchSimple, Sport, PeriodScore} from '../../../utils/types';

import './styles.scss';

// BIQ: split this file & documentation

export interface MatchListItemProps extends GenericProps {
  match: MatchSimple;
  hasDate?: boolean;
}

interface MatchListItemWrapperProps extends GenericProps {
  id: string;
  slug: string;
}

const MatchListItemWrapper: FC<PropsWithChildren<MatchListItemWrapperProps>> = ({
  id,
  slug,
  children,
  className,
}: PropsWithChildren<MatchListItemWrapperProps>) =>
  id ? (
    <Link to={`/${slug}/${id}`} className={`MatchListItem__wrapper ${className ?? ''}`}>
      {children}
    </Link>
  ) : (
    <div className={`MatchListItem__wrapper ${className ?? ''}`}>{children}</div>
  );

const MatchDay: FC<{date: Date}> = ({date}: {date: Date}) => (
  <span className="MatchListItem__date MatchListItem__date--day">
    {date.toLocaleString('el-GR', {month: 'short'})} {date.getDate()}
  </span>
);

const MatchYear: FC<{date: Date}> = ({date}: {date: Date}) => (
  <span className="MatchListItem__date MatchListItem__date--year">{date.getFullYear()}</span>
);

const MatchListItemNotStarted: FC<MatchListItemProps> = ({
  match: {_id, sportEventSlug, date, teams},
  hasDate,
}: MatchListItemProps) => (
  <MatchListItemWrapper className="MatchListItem__wrapper--simple" id={_id} slug={sportEventSlug}>
    {hasDate && <MatchDay date={date} />}
    <h5 className="MatchListItem__team MatchListItem__team--home">
      {hasDate ? teams[0].shortname : teams[0].fullname}
    </h5>
    <div className="MatchListItem__time">{`${date.getHours()}:${date
      .getMinutes()
      .toString()
      .padStart(2, '0')}`}</div>
    <h5 className="MatchListItem__team MatchListItem__team--away">
      {hasDate ? teams[1].shortname : teams[1].fullname}
    </h5>
    {hasDate && <MatchYear date={date} />}
  </MatchListItemWrapper>
);

const MatchListItemWithScore: FC<MatchListItemProps> = ({
  match: {_id, sportEventSlug, date, score, status, matchStatus, minute, stoppageTime, teams},
  hasDate,
}: MatchListItemProps) => (
  <MatchListItemWrapper id={_id} slug={sportEventSlug}>
    {hasDate && <MatchDay date={date} />}
    <h5 className="MatchListItem__team MatchListItem__team--home MatchListItem__withScore">
      {hasDate ? teams[0].shortname : teams[0].fullname}
    </h5>
    <div
      className={`MatchListItem__score ${
        score[0] > score[1] ? 'MatchListItem__score--winning' : ''
      }`}
    >
      {score[0]}
    </div>
    <div
      className={`MatchListItem__score ${
        score[1] > score[0] ? 'MatchListItem__score--winning' : ''
      }`}
    >
      {score[1]}
    </div>
    <h5 className="MatchListItem__team MatchListItem__team--away">
      {hasDate ? teams[1].shortname : teams[1].fullname}
    </h5>
    {hasDate && <MatchYear date={date} />}
    {!hasDate && (
      <span className="MatchListItem__status">
        {status === EventStatus.LIVE &&
        minute !== null &&
        minute !== undefined &&
        matchStatus !== 'ΗΜΙ'
          ? `${minute}'${stoppageTime ? ` + ${stoppageTime}` : ''}`
          : matchStatus}
      </span>
    )}
  </MatchListItemWrapper>
);

const MatchListItemWithPeriods: FC<MatchListItemProps> = ({
  match: {_id, date, sportEventSlug, sport, status, matchStatus, teams, periodScores, score, flags},
}: MatchListItemProps) => (
  <MatchListItemWrapper
    className={`MatchListItem__wrapper--with-periods ${
      status === EventStatus.NOT_STARTED ? 'MatchListItem__wrapper--with-periods-not-live' : ''
    }`}
    id={_id}
    slug={sportEventSlug}
  >
    <div className="MatchListItem__header">
      {sport !== Sport.TENNIS && <div className="MatchListItem__headerSpacer" />}
      <div
        className={`MatchListItem__status ${
          status === EventStatus.LIVE ? 'MatchListItem__status--live' : ''
        }`}
      >
        {matchStatus}
      </div>
      {sport === Sport.TENNIS && <div>S</div>}
      <div className="MatchListItem__periods">
        {periodScores.map(ps => (
          <span className="MatchListItem__periodNumber" key={ps.periodNumber}>
            {ps.periodNumber}
          </span>
        ))}
      </div>
    </div>
    {(['homeScore', 'awayScore'] as (keyof Pick<PeriodScore, 'homeScore' | 'awayScore'>)[]).map(
      (teamKey, i) => (
        <div className="MatchListItem__row" key={teamKey}>
          <div className="MatchListItem__team">
            {sport === Sport.TENNIS && (
              <div className="MatchListItem__flag">
                {flags && flags[i] && <img src={flags[i]} alt={teams[i].fullname} />}{' '}
              </div>
            )}
            <span className="MatchListItem__name">{teams[i].fullname}</span>
          </div>
          {status !== EventStatus.NOT_STARTED && (
            <>
              <div
                className={`MatchListItem__score ${
                  score[i] > score[Math.abs(1 - i)] ? 'MatchListItem__score--winning' : '' // BIQ: awful hack for dynamic comparison in order to keep map
                }`}
              >
                {score[i]}
              </div>
              <div className="MatchListItem__periodScores">
                {periodScores.map(ps => {
                  const opponentKey = i ? 'homeScore' : 'awayScore';
                  const teamScore = ps[teamKey];
                  const oppScore = ps[opponentKey];

                  const winning = teamScore && oppScore && teamScore > oppScore;

                  return (
                    <span
                      className={`MatchListItem__periodScore ${
                        winning ? 'MatchListItem__periodScore--winning' : ''
                      }`}
                      key={ps.periodNumber}
                    >
                      {teamKey in ps ? ps[teamKey] : `${status === EventStatus.CLOSED ? '-' : ''}`}
                    </span>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )
    )}
    {status === EventStatus.NOT_STARTED && (
      <div className="MatchListItem__time">{`${date.getHours()}:${date
        .getMinutes()
        .toString()
        .padStart(2, '0')}`}</div>
    )}
  </MatchListItemWrapper>
);

const MatchListItem: FC<MatchListItemProps> = ({match, hasDate, className}: MatchListItemProps) => (
  <article
    className={`MatchListItem MatchListItem--${match.sport} ${
      match.status === EventStatus.LIVE ? 'MatchListItem--live' : ''
    } ${className ?? ''}`}
  >
    {/* eslint-disable no-nested-ternary */}
    {[Sport.SOCCER, Sport.HANDBALL].includes(match.sport) &&
    match.status === EventStatus.NOT_STARTED ? (
      <MatchListItemNotStarted match={match} hasDate={hasDate} />
    ) : [Sport.SOCCER, Sport.HANDBALL].includes(match.sport) ? (
      <MatchListItemWithScore match={match} hasDate={hasDate} />
    ) : (
      <MatchListItemWithPeriods match={match} />
    )}
  </article>
);

export default MatchListItem;
